import { chSupabase } from '../lib/supabase';
import type { ChNotification, ChNotificationType } from '../types';

/**
 * Where a notification should take you: the project with the item open for
 * the team, the project's review page for clients.
 */
export async function resolveNotificationTarget(
  notif: Pick<ChNotification, 'post_id' | 'project_id'>,
  isStaff: boolean
): Promise<string | null> {
  let projectId = notif.project_id || null;
  if (!projectId && notif.post_id) {
    const { data } = await chSupabase.from('ch_posts').select('project_id').eq('id', notif.post_id).maybeSingle();
    projectId = (data as { project_id?: string } | null)?.project_id || null;
  }
  if (!projectId) return null;
  const { data: project } = await chSupabase.from('ch_projects').select('slug').eq('id', projectId).maybeSingle();
  const slug = (project as { slug?: string } | null)?.slug;
  if (!slug) return null;
  if (!isStaff) return `/contenthub/projeto/${slug}/review`;
  return notif.post_id ? `/contenthub/projetos/${slug}?post=${notif.post_id}` : `/contenthub/projetos/${slug}`;
}

export const chNotificationService = {
  async getByUser(userId: string): Promise<ChNotification[]> {
    const { data, error } = await chSupabase
      .from('ch_notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) throw error;
    return (data || []) as ChNotification[];
  },

  async getUnreadCount(userId: string): Promise<number> {
    const { count, error } = await chSupabase
      .from('ch_notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('read', false);

    if (error) throw error;
    return count || 0;
  },

  async markAsRead(id: string): Promise<void> {
    const { error } = await chSupabase
      .from('ch_notifications')
      .update({ read: true })
      .eq('id', id);

    if (error) throw error;
  },

  async markAllAsRead(userId: string): Promise<void> {
    const { error } = await chSupabase
      .from('ch_notifications')
      .update({ read: true })
      .eq('user_id', userId)
      .eq('read', false);

    if (error) throw error;
  },

  async create(notification: {
    user_id: string;
    post_id: string | null;
    project_id?: string | null;
    type: ChNotificationType;
    message: string;
  }): Promise<void> {
    const { error } = await chSupabase
      .from('ch_notifications')
      .insert({ ...notification, read: false });

    // PGRST204: project_id column not created yet (mentions SQL not applied)
    if (error?.code === 'PGRST204' && 'project_id' in notification) {
      const { project_id: _omit, ...legacy } = notification;
      const { error: retryError } = await chSupabase
        .from('ch_notifications')
        .insert({ ...legacy, read: false });
      if (retryError) throw retryError;
      return;
    }
    if (error) throw error;
  },

  /** Notifies the people tagged in one of your comments. */
  async notifyMentions(commentId: string): Promise<void> {
    const { error } = await chSupabase.rpc('ch_notify_mentions', { p_comment_id: commentId });
    // PGRST202: function not created yet — mentions are saved, just not notified
    if (error && error.code !== 'PGRST202') throw error;
  },

  /** Staff telling a project's clients about a reply or update. */
  async notifyProjectClients(projectId: string, postId: string | null, type: ChNotificationType, message: string): Promise<void> {
    const { error } = await chSupabase.rpc('ch_notify_project_clients', {
      p_project_id: projectId,
      p_post_id: postId,
      p_type: type,
      p_message: message,
    });
    if (error && error.code !== 'PGRST202') throw error;
  },

  /**
   * Notifies the project's team (and, when no staff member is linked to the
   * project, every Admin/Account). Clients are only included on request.
   */
  async notifyProjectMembers(
    projectId: string,
    excludeUserId: string,
    postId: string | null,
    type: ChNotificationType,
    message: string,
    includeClients = false
  ): Promise<void> {
    const { error: rpcError } = await chSupabase.rpc('ch_notify_project', {
      p_project_id: projectId,
      p_post_id: postId,
      p_type: type,
      p_message: message,
      p_include_clients: includeClients,
    });
    if (!rpcError) return;
    // PGRST202: function not created yet (permissions SQL not applied)
    if (rpcError.code !== 'PGRST202') throw rpcError;

    const { data: members } = await chSupabase
      .from('ch_user_projects')
      .select('user_id')
      .eq('project_id', projectId);

    if (!members || members.length === 0) return;

    const notifications = members
      .filter(m => m.user_id !== excludeUserId)
      .map(m => ({
        user_id: m.user_id,
        post_id: postId,
        type,
        message,
        read: false,
      }));

    if (notifications.length === 0) return;

    const { error } = await chSupabase
      .from('ch_notifications')
      .insert(notifications);

    if (error) throw error;
  },

  subscribeToNew(userId: string, callback: (notification: ChNotification) => void) {
    const channel = chSupabase
      .channel(`notifications:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'ch_notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          callback(payload.new as ChNotification);
        }
      )
      .subscribe();

    return () => {
      chSupabase.removeChannel(channel);
    };
  },
};
